/** Mirrors FunkySystem.Object.Enum.VoucherType */
export const VoucherType = {
	Default: 0,
	GodziSlots: 1,
};

/** Mirrors FunkySystem.Object.Enum.EventStatusType */
export const EventStatusType = {
	All: 0,
	Active: 1,
	Closed: 2,
};

/** UI-only sentinel for the status filter; do not send directly to SearchVoucherEvent. */
export const VOUCHER_EVENT_STATUS_ALL = 'ALL';

export const VOUCHER_TYPE_OPTIONS = [
	{ value: VoucherType.Default, label: 'Default (0)' },
	{ value: VoucherType.GodziSlots, label: 'GodziSlots (1)' },
];

export const EVENT_STATUS_FILTER_OPTIONS = [
	{ value: VOUCHER_EVENT_STATUS_ALL, label: 'All' },
	{ value: EventStatusType.Active, label: 'Active' },
	{ value: EventStatusType.Closed, label: 'Closed' },
];

export function isAllEventStatusFilter(status) {
	return status === VOUCHER_EVENT_STATUS_ALL || status === EventStatusType.All;
}

const VOUCHER_TYPE_LABELS = {
	[VoucherType.Default]: 'Default',
	[VoucherType.GodziSlots]: 'GodziSlots',
};

const EVENT_STATUS_LABELS = {
	[EventStatusType.All]: 'All',
	[EventStatusType.Active]: 'Active',
	[EventStatusType.Closed]: 'Closed',
};

export const DEFAULT_CREATE_FORM = {
	eventName: '',
	voucherType: VoucherType.GodziSlots,
	includeSubLineFpIds: [],
	singleFpIds: [],
	stakeLevel: 3,
};

export function formatVoucherType(value) {
	const num = Number(value);
	if (Number.isNaN(num)) return String(value ?? '—');
	const label = VOUCHER_TYPE_LABELS[num];
	return label ? `${label} (${num})` : String(num);
}

export function formatEventStatus(value) {
	const num = Number(value);
	if (Number.isNaN(num)) return String(value ?? '—');
	return EVENT_STATUS_LABELS[num] ?? String(num);
}

export function formatFpIdList(value) {
	if (!Array.isArray(value) || value.length === 0) return '—';
	return value.join(', ');
}

/** Ops-friendly single-line summary of event targeting. */
export function formatTargetFpIds(includeSubLineFpIds, singleFpIds) {
	const subLine = Array.isArray(includeSubLineFpIds) ? includeSubLineFpIds : [];
	const single = Array.isArray(singleFpIds) ? singleFpIds : [];
	if (subLine.length === 0 && single.length === 0) return '—';
	const parts = [];
	if (subLine.length > 0) parts.push(`Sub-line: ${subLine.join(', ')}`);
	if (single.length > 0) parts.push(`Single: ${single.join(', ')}`);
	return parts.join(' · ');
}

export function filterEventsByName(events, query) {
	const needle = String(query ?? '').trim().toLowerCase();
	if (!needle) return events;
	return events.filter((event) => {
		const name = String(event.eventName ?? '').toLowerCase();
		const id = String(event.eventId ?? event.EventId ?? '').toLowerCase();
		return name.includes(needle) || id.includes(needle);
	});
}

export function filterEventsByStatus(events, status) {
	if (isAllEventStatusFilter(status)) return events;
	const target = Number(status);
	return events.filter((event) => Number(event.status) === target);
}

export function mergeVoucherEventLists(...lists) {
	const seen = new Set();
	const merged = [];
	for (const list of lists) {
		for (const event of list) {
			const key = event?.eventId || event?.EventId || eventRowId(event);
			if (seen.has(key)) continue;
			seen.add(key);
			merged.push(event);
		}
	}
	return merged;
}

export function parseFpId(value) {
	const trimmed = String(value ?? '').trim();
	if (!trimmed) return null;
	if (!/^\d+$/.test(trimmed)) return null;
	const num = Number(trimmed);
	if (!Number.isSafeInteger(num) || num <= 0) return null;
	return num;
}

export function validateFpIdChip(value) {
	if (!parseFpId(value)) return 'FpId must be a positive integer';
	return '';
}

export function buildCreateBody(form) {
	return {
		eventName: String(form.eventName ?? '').trim(),
		voucherType: Number(form.voucherType),
		includeSubLineFpIds: [...(form.includeSubLineFpIds ?? [])].map(Number),
		singleFpIds: [...(form.singleFpIds ?? [])].map(Number),
		stakeLevel: Number(form.stakeLevel),
	};
}

export function validateCreateForm(form) {
	const body = buildCreateBody(form);
	if (!body.eventName) return 'Event name is required';
	if (!Number.isInteger(body.voucherType)) return 'Voucher type is required';
	if (!Number.isInteger(body.stakeLevel) || body.stakeLevel < 0) return 'Stake level must be a non-negative integer';
	if (body.includeSubLineFpIds.length === 0 && body.singleFpIds.length === 0) {
		return 'Add at least one FpId under Sub-line or Single operators.';
	}
	if (body.includeSubLineFpIds.some((id) => !Number.isInteger(id) || id <= 0)) {
		return 'Include Sub-Line FpIds must be positive integers';
	}
	if (body.singleFpIds.some((id) => !Number.isInteger(id) || id <= 0)) {
		return 'Single FpIds must be positive integers';
	}
	return '';
}

export function normalizeVoucherEventList(data) {
	if (Array.isArray(data)) return data;
	if (data && Array.isArray(data.list)) return data.list;
	if (data && Array.isArray(data.events)) return data.events;
	if (data && Array.isArray(data.data)) return data.data;
	return [];
}

export function eventRowId(event) {
	return event?.eventId || event?.EventId || `${event?.eventName}-${event?.createdOn}`;
}
